import { useState, useCallback, useMemo } from "react";
import { useStudentDepletionStatus } from "@/hooks/useStudentDepletionStatus";
import { DepletionFilters } from "./DepletionFilters";
import { DepletionSummaryCards } from "./DepletionSummaryCards";
import { CrmSummaryCard } from "./CrmSummaryCard";
import { DepletionTable, SortField, SortDirection } from "./DepletionTable";
import { DepletionTableFilters, StatusFilter, TopupFilter } from "./DepletionTableFilters";
import { startOfMonth, endOfMonth, format } from "date-fns";
import { Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import * as XLSX from "xlsx";

export function DepletionStatusContent() {
  // Default to current month
  const [startDate, setStartDate] = useState(() => startOfMonth(new Date()));
  const [endDate, setEndDate] = useState(() => endOfMonth(new Date()));
  
  // Track the dates that are actually being used for the query
  const [queryStartDate, setQueryStartDate] = useState(() => 
    format(startOfMonth(new Date()), "yyyy-MM-dd")
  );
  const [queryEndDate, setQueryEndDate] = useState(() => 
    format(endOfMonth(new Date()), "yyyy-MM-dd")
  );

  // Filter states
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [topupFilter, setTopupFilter] = useState<TopupFilter>('all');

  // Sort states
  const [sortField, setSortField] = useState<SortField>(null);
  // Note: SortField now includes 'estimated_hours_depletion_date'
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  const { data, isLoading, lastFetchedAt, refresh } = useStudentDepletionStatus({
    startDate: queryStartDate,
    endDate: queryEndDate,
  });

  const handleSearch = useCallback(() => {
    setQueryStartDate(format(startDate, "yyyy-MM-dd"));
    setQueryEndDate(format(endDate, "yyyy-MM-dd"));
  }, [startDate, endDate]);

  const handleRefresh = useCallback(() => {
    refresh();
  }, [refresh]);



  const handleSort = useCallback((field: SortField) => {
    if (sortField === field) {
      // Toggle direction or reset
      if (sortDirection === 'asc') {
        setSortDirection('desc');
      } else {
        setSortField(null);
        setSortDirection('asc');
      }
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  }, [sortField, sortDirection]);

  // Process students with filters and sorting
  const processedStudents = useMemo(() => {
    if (!data?.students) return [];

    let result = [...data.students];

    // Apply status filter
    if (statusFilter !== 'all') {
      result = result.filter(s => s.status === statusFilter);
    }

    // Note: 'predicted' status is now supported in the filter

    // Apply topup filter
    if (topupFilter !== 'all') {
      if (topupFilter === 'has_topup') {
        result = result.filter(s => s.next_topup !== null);
      } else {
        result = result.filter(s => s.next_topup === null);
      }
    }

    // Apply sorting
    if (sortField) {
      result.sort((a, b) => {
        let valueA: string | number;
        let valueB: string | number;

        switch (sortField) {
          case 'student_code':
            valueA = a.student_code;
            valueB = b.student_code;
            break;
          case 'event_date':
            valueA = new Date(a.event_date).getTime();
            valueB = new Date(b.event_date).getTime();
            break;
          case 'after_hours_at_event':
            valueA = a.after_hours_at_event;
            valueB = b.after_hours_at_event;
            break;
          case 'current_hours':
            valueA = a.current_hours ?? -Infinity;
            valueB = b.current_hours ?? -Infinity;
            break;
          case 'estimated_hours_depletion_date':
            valueA = a.estimated_hours_depletion_date ? new Date(a.estimated_hours_depletion_date).getTime() : Infinity;
            valueB = b.estimated_hours_depletion_date ? new Date(b.estimated_hours_depletion_date).getTime() : Infinity;
            break;
          default:
            return 0;
        }

        if (sortDirection === 'asc') {
          return valueA < valueB ? -1 : valueA > valueB ? 1 : 0;
        } else {
          return valueA > valueB ? -1 : valueA < valueB ? 1 : 0;
        }
      });
    }

    return result;
  }, [data?.students, statusFilter, topupFilter, sortField, sortDirection]);

  const totalCount = data?.students?.length ?? 0;

  const handleExport = useCallback(() => {
    if (!processedStudents.length) return;

    const statusMap: Record<string, string> = {
      depleted: 'หมด',
      nearly_depleted: 'ใกล้หมด',
      predicted: 'คาดว่าจะหมด',
    };
    const crmStatusMap: Record<string, string> = {
      RENEWED: 'ต่อแล้ว',
      WILL_RENEW: 'จะต่อ',
      UNDECIDED: 'ยังไม่ตัดสินใจ',
      NOT_RENEWING: 'ไม่ต่อ',
    };

    const rows = processedStudents.map(s => ({
      'รหัสนักเรียน': s.student_code,
      'ชื่อเล่น': s.nickname ?? '',
      'ชื่อเต็ม': s.student_name,
      'สถานะ': statusMap[s.status] ?? s.status,
      'วันที่หมด/ใกล้หมด': s.event_date?.slice(0, 10) ?? '',
      'คาดว่าจะหมด': s.estimated_hours_depletion_date?.slice(0, 10) ?? '',
      'ชม.ณ เวลานั้น': s.after_hours_at_event,
      'ชม.ปัจจุบัน': s.current_hours ?? '',
      'เรียน ชม./สัปดาห์': s.weekly_scheduled_hours ?? '',
      'เติมชม.ล่าสุด': s.next_topup ? `${s.next_topup.value} (${s.next_topup.created_at})` : '',
      'ยอดซื้อรวม': s.total_purchase ?? '',
      'สถานะ CRM': s.crm_info ? (crmStatusMap[s.crm_info.renewal_status] ?? s.crm_info.renewal_status) : '',
      'วันที่โทร': s.crm_info?.call_date?.slice(0, 10) ?? '',
    }));

    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "สถานะหมดคอร์ส");
    XLSX.writeFile(wb, `สถานะหมดคอร์ส_${queryStartDate}_${queryEndDate}.xlsx`);
  }, [processedStudents, queryStartDate, queryEndDate]);

  return (
    <div className="space-y-6">
      {/* Date Filters */}
      <DepletionFilters
        startDate={startDate}
        endDate={endDate}
        onStartDateChange={setStartDate}
        onEndDateChange={setEndDate}
        onSearch={handleSearch}
        onRefresh={handleRefresh}
        lastFetchedAt={lastFetchedAt}
        isLoading={isLoading}
      />

      {/* Summary Cards */}
      <div className="grid gap-6 md:grid-cols-2">
      <DepletionSummaryCards 
          summary={data?.summary} 
          isLoading={isLoading}
          startDate={queryStartDate}
          endDate={queryEndDate}
        />
        <CrmSummaryCard
          crmSummary={data?.summary?.crm_summary}
          isLoading={isLoading}
        />
      </div>

      {/* Table Filters + Export */}
      <div className="flex items-end justify-between gap-4">
        <DepletionTableFilters
          statusFilter={statusFilter}
          topupFilter={topupFilter}
          onStatusFilterChange={setStatusFilter}
          onTopupFilterChange={setTopupFilter}
          totalCount={totalCount}
          filteredCount={processedStudents.length}
        />
        <Button
          variant="outline"
          size="sm"
          onClick={handleExport}
          disabled={processedStudents.length === 0}
        >
          <Download className="h-4 w-4 mr-1" />
          Export Excel
        </Button>
      </div>

      {/* Note about data sources */}
      <div className="text-sm text-muted-foreground bg-muted/50 rounded-lg px-4 py-2">
        <span className="font-medium">หมายเหตุ:</span> ยอดซื้อรวม = ยอดต่อคอร์ส (ตั้งแต่ พ.ค. 2025) + ยอดซื้อใหม่ (ตั้งแต่ พ.ย. 2025)
      </div>

      {/* Table */}
      <DepletionTable 
        students={processedStudents} 
        isLoading={isLoading}
        sortField={sortField}
        sortDirection={sortDirection}
        onSort={handleSort}
      />
    </div>
  );
}
